import { useEffect, useState } from "react";
import { ArrowLeft, Phone, Pencil, GraduationCap, Users, Clock, ArrowRight, User } from "lucide-react";
import CRM_API from "../../api/crmApi";
import CrmFollowupPanel from "./CrmFollowupPanel";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";

const InfoRow = ({ label, value }) => (
  <div className="mb-4">
    <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-0.5">{label}</div>
    <div className={`text-sm font-medium ${value ? "text-gray-900" : "text-gray-300"}`}>{value || "—"}</div>
  </div>
);

export default function CrmLeadProfile({ leadId, onBack, onNavigate }) {
  const [lead, setLead] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [assignedTo, setAssignedTo] = useState(null);
  const [users, setUsers] = useState([]);
  const [toUser, setToUser] = useState("");

  const isAdmin = localStorage.getItem("crm_role") === "admin";

  const fetchLead = async () => {
    try {
      const res = await CRM_API.get(`/leads/${leadId}`);
      setLead(res.data.lead);
      setFollowups(res.data.followups || []);
      setAssignedTo(res.data.assigned_to);
    } catch (error) { console.log("Error loading lead", error); }
  };

  const fetchUsers = async () => {
    try {
      const res = await CRM_API.get("/users");
      setUsers(res.data || []);
    } catch (error) { console.log("Error loading users", error); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLead(); fetchUsers(); }, [leadId]);

  const transferLead = async () => {
    if (!toUser) { alert("Select counsellor"); return; }
    try {
      await CRM_API.post(`/leads/move/${leadId}?to_user=${toUser}`);
      alert("Lead transferred");
      fetchLead();
    } catch (error) { console.log(error); }
  };

  if (!lead) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <div className="h-5 w-5 rounded-full border-2 border-blue-400 border-t-transparent animate-spin mr-2" />
        Loading lead…
      </div>
    );
  }

  const initials = lead.student_name
    ? lead.student_name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  const tempVariant = lead.temperature === "hot" ? "hot" : lead.temperature === "warm" ? "warm" : lead.temperature === "cold" ? "cold" : "secondary";
  const tempEmoji = lead.temperature === "hot" ? "🔥" : lead.temperature === "warm" ? "🟡" : "🔵";

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Back */}
      <Button variant="outline" size="sm" onClick={onBack}>
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to Leads
      </Button>

      {/* Hero Card */}
      <Card className="overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500" />
        <CardContent className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            {/* Avatar + info */}
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white font-extrabold text-xl shrink-0">
                {initials}
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-0.5">{lead.student_name}</h4>
                <p className="text-sm text-gray-500 mb-2">
                  <Phone className="inline h-3.5 w-3.5 mr-1" />{lead.student_contact}
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Badge variant="pending">{lead.status}</Badge>
                  <Badge variant={tempVariant}>{tempEmoji} {lead.temperature}</Badge>
                  {assignedTo && (
                    <span className="text-xs text-gray-500 self-center">
                      <User className="inline h-3 w-3 mr-0.5" />{assignedTo}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 flex-wrap">
              <a
                href={`tel:${lead.student_contact}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 transition-colors"
              >
                <Phone className="h-4 w-4" /> Call Now
              </a>
              <Button variant="warning" onClick={() => onNavigate("crm-edit-lead", leadId)}>
                <Pencil className="h-4 w-4 mr-1" /> Edit Lead
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

        {/* Left — 2 cols */}
        <div className="lg:col-span-2 space-y-6">

          {/* Academic Profile */}
          <Card>
            <CardHeader className="border-b border-gray-100 pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <GraduationCap className="h-4 w-4 text-blue-600" />
                Academic Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-x-6">
                <InfoRow label="Interested Course" value={lead.interested_course} />
                <InfoRow label="Current Qualification" value={lead.qualification} />
                <InfoRow label="School / College" value={lead.school_name} />
                <InfoRow label="Year of Passing" value={lead.year_of_passing} />
                <InfoRow label="Preferred Location" value={lead.interested_location} />
                <InfoRow label="Lead Source" value={lead.lead_source} />
              </div>
            </CardContent>
          </Card>

          {/* Parent Info */}
          <Card>
            <CardHeader className="border-b border-gray-100 pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4 text-cyan-600" />
                Personal &amp; Parent Info
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-x-6">
                <InfoRow label="Gender" value={lead.gender} />
                <InfoRow label="Parent Name" value={lead.parent_name} />
                <InfoRow label="Parent Contact" value={lead.parent_contact} />
                <InfoRow label="City" value={lead.city} />
                <InfoRow label="State" value={lead.state} />
                <InfoRow label="Country" value={lead.country} />
                <div className="col-span-2"><InfoRow label="Address" value={lead.address} /></div>
              </div>
            </CardContent>
          </Card>

          {/* Followup Timeline */}
          <Card>
            <CardHeader className="border-b border-gray-100 pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="h-4 w-4 text-violet-600" />
                Followup Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {followups.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Clock className="mx-auto h-8 w-8 mb-2 opacity-30" />
                  <p className="text-sm">No followups recorded yet.</p>
                </div>
              ) : (
                <div>
                  {followups.map((f, idx) => (
                    <div key={f.id} className="flex gap-3 pb-5 last:pb-0">
                      {/* Timeline dot */}
                      <div className="flex flex-col items-center shrink-0">
                        <div className="h-2.5 w-2.5 rounded-full bg-blue-500 mt-1" />
                        {idx < followups.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
                      </div>
                      {/* Content */}
                      <div className="flex-1 rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                          <span className="text-sm font-bold text-blue-600">{f.status}</span>
                          <div className="flex gap-1.5 items-center">
                            <Badge variant={f.temperature || "secondary"} className="text-xs">{f.temperature}</Badge>
                            {f.next_followup && <span className="text-xs text-gray-500">{f.next_followup}</span>}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mb-1">{f.remark}</p>
                        <p className="text-xs text-gray-400">Updated by {f.updated_by}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

        </div>

        {/* Right — 1 col */}
        <div className="space-y-6">

          {/* Log Followup */}
          <Card>
            <CardHeader className="border-b border-gray-100 pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="h-4 w-4 text-blue-600" />
                Log Followup
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <CrmFollowupPanel leadId={leadId} refresh={fetchLead} />
            </CardContent>
          </Card>

          {/* Transfer Lead — admin only */}
          {isAdmin && (
            <Card>
              <CardHeader className="border-b border-gray-100 pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ArrowRight className="h-4 w-4 text-red-600" />
                  Transfer Lead
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                    Select Counsellor
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={toUser}
                    onChange={(e) => setToUser(e.target.value)}
                  >
                    <option value="">— Choose counsellor —</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.full_name}</option>
                    ))}
                  </select>
                </div>
                <Button variant="destructive" className="w-full" onClick={transferLead}>
                  <ArrowRight className="h-4 w-4 mr-1" /> Transfer Lead
                </Button>
              </CardContent>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
}
